import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { removeItem, setItem } from "../helpers/data_management";
import { updateCurrentSalaryDialog } from "../redux/actions/dialog_action";
import { newGetUserInfo } from "../redux/actions/user_action";
import * as userType from "../redux/types/user_type";

function validateJWT(token) {
  try {
    if (!token || typeof token !== "string" || token.split(".").length !== 3) {
      console.error("Invalid JWT token format");
      return null;
    }

    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const parsedPayload = JSON.parse(decoded);

    // Check if token has required fields
    if (
      !parsedPayload ||
      typeof parsedPayload !== "object" ||
      !parsedPayload.user_id
    ) {
      console.error("JWT token missing required fields");
      return null;
    }

    // Check token expiration - handle both standard 'exp' and custom 'expiry_at' fields
    const currentTime = Date.now();

    // Standard JWT exp field (Unix timestamp in seconds)
    if (parsedPayload.exp && parsedPayload.exp * 1000 < currentTime) {
      console.error("JWT token has expired (exp field)");
      return null;
    }

    // Custom expiry_at field (ISO string format)
    if (parsedPayload.expiry_at) {
      const expiryTime = new Date(parsedPayload.expiry_at).getTime();
      if (expiryTime < currentTime) {
        console.error("JWT token has expired (expiry_at field)");
        return null;
      }
    }

    return parsedPayload;
  } catch (e) {
    console.error("JWT token validation failed:", e);
    return null;
  }
}

/**
 * Custom hook to handle auto-apply job from email link
 * @param {Object} params - Configuration object
 * @param {boolean} params.shouldAutoApply - Flag from URL (apply_job=true)
 * @param {string} params.jobId - Job ID from URL
 * @param {string} params.authorizationToken - JWT token from URL
 * @param {Object} params.job - Current job object
 * @param {boolean} params.isLoggedIn - User authentication status
 * @param {string|null} params.currentUserId - Current user ID
 * @param {Function} params.applyJobFunc - Function to apply for job
 * @param {Function} params.triggerSnackbar - Function to show snackbar messages
 * @param {string} params.jobOrigin - Origin of job application
 */
function useAutoApply({
  shouldAutoApply,
  jobId,
  authorizationToken,
  job,
  isLoggedIn,
  currentUserId,
  applyJobFunc,
  triggerSnackbar,
  jobOrigin,
}) {
  const [autoApplyAttempted, setAutoApplyAttempted] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      !shouldAutoApply ||
      !jobId ||
      !authorizationToken ||
      !job?.id ||
      (jobId !== job.id && jobId !== job.slug)
    ) {
      return;
    }

    const autoLoginSessionKey = `auto_login_session_${authorizationToken}`;

    const markAutoLoginHandled = () => {
      if (!autoLoginSessionKey) {
        return;
      }
      try {
        sessionStorage.setItem(autoLoginSessionKey, "true");
      } catch (storageError) {
        console.error("Auto-login session flag failed:", storageError);
      }
    };

    // Check if auto-login has already been handled in this session
    try {
      if (sessionStorage.getItem(autoLoginSessionKey) === "true") {
        return;
      }
    } catch (storageError) {
      console.error("Auto-login session state read failed:", storageError);
    }

    const decoded = validateJWT(authorizationToken);
    if (!decoded || !decoded.user_id) {
      triggerSnackbar({
        snackbarMessage:
          "Invalid or expired authorization link. Please try again.",
        severity: "error",
      });
      markAutoLoginHandled();
      return;
    }

    const tokenUserId = String(decoded.user_id);
    const currentToken = Cookies.get("token");

    // Create unique key for this auto-apply attempt
    const autoApplyKey = `auto_applied_${job.id}_${tokenUserId}`;
    const hasAutoApplied = localStorage.getItem(autoApplyKey);

    // Enhanced condition: Check both user ID and token match
    if (
      isLoggedIn &&
      currentUserId &&
      currentUserId === tokenUserId &&
      currentToken === authorizationToken &&
      !hasAutoApplied &&
      !autoApplyAttempted
    ) {
      setAutoApplyAttempted(true);
      localStorage.setItem(autoApplyKey, "true");
      localStorage.setItem(`${autoApplyKey}_timestamp`, Date.now().toString());

      // Auto-apply the job
      const params = {
        jobId: job.id || job.slug,
        canCancel: false,
        origin: jobOrigin,
        jobDiscovery: "email_auto_apply",
        applySource: "email_auto_apply",
      };

      applyJobFunc(job, params)
        .then((response) => {
          if (response && response.success) {
            triggerSnackbar({
              snackbarMessage: "Application successful!",
              severity: "success",
            });
          } else {
            localStorage.removeItem(autoApplyKey);
            localStorage.removeItem(`${autoApplyKey}_timestamp`);
            triggerSnackbar({
              snackbarMessage: "You have already applied to this job.",
              severity: "error",
            });
          }
          markAutoLoginHandled();
        })
        .catch((error) => {
          localStorage.removeItem(autoApplyKey);
          localStorage.removeItem(`${autoApplyKey}_timestamp`);
          console.error("Auto-apply error:", error);
          triggerSnackbar({
            snackbarMessage:
              "Application failed. Please try applying manually.",
            severity: "error",
          });
          markAutoLoginHandled();
        });
    } else if (
      !isLoggedIn ||
      !currentUserId ||
      currentUserId !== tokenUserId ||
      currentToken !== authorizationToken
    ) {
      // Don't proceed if already auto-applied with this token
      if (hasAutoApplied) {
        triggerSnackbar({
          snackbarMessage:
            "You have already applied to this job using this link.",
          severity: "info",
        });
        markAutoLoginHandled();
        return;
      }

      // Clear authentication state for account switching (avoid full logout side effects)
      removeItem(userType.USER_TOKEN);
      removeItem(userType.USER_PROFILE);
      Cookies.remove("token");
      Cookies.remove(process.env.NEXT_PUBLIC_JWT_COOKIE, {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || ".hiredly.com",
        path: "/",
      });
      // Clear user state in Redux without triggering full logout flow
      dispatch({ type: userType.LOGOUT });

      // Small delay to ensure state is cleared
      setTimeout(() => {
        // Disable current salary dialog popup on this page
        localStorage.removeItem("showCurrentSalaryDialogAfterJump");

        // Set the new token
        Cookies.set("token", authorizationToken);
        Cookies.set(process.env.NEXT_PUBLIC_JWT_COOKIE, authorizationToken, {
          domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || ".hiredly.com",
          path: "/",
          expires: 7,
        });
        setItem(userType.USER_TOKEN, authorizationToken);

        // Fetch user details for the new token
        dispatch(newGetUserInfo())
          .then(() => {
            // Ensure salary dialog stays closed on this page
            dispatch(updateCurrentSalaryDialog(false));
            // User logged in, effect will re-run for auto-apply
          })
          .catch((error) => {
            console.error("Auto-login failed:", error);

            // Cleanup on failure
            Cookies.remove("token");
            Cookies.remove(process.env.NEXT_PUBLIC_JWT_COOKIE);
            removeItem(userType.USER_TOKEN);

            triggerSnackbar({
              snackbarMessage:
                "Auto-login failed. Please try logging in manually.",
              severity: "error",
            });
            markAutoLoginHandled();
          });
      }, 100); // Small delay to ensure cleanup completes
    }
  }, [
    shouldAutoApply,
    jobId,
    authorizationToken,
    job?.id,
    job?.slug,
    isLoggedIn,
    currentUserId,
    autoApplyAttempted,
    applyJobFunc,
    job,
    jobOrigin,
    triggerSnackbar,
    dispatch,
  ]);

  return { autoApplyAttempted };
}

export default useAutoApply;
